import * as Sentry from '@sentry/react';
import { applyMiddleware, compose, createStore } from 'redux';
import thunkMiddleware from 'redux-thunk';
import { userConstants } from './constants';
import { config } from '@/config';
import reducers from './reducers';

declare global {
  interface Window {
    __PRELOADED_STATE__: any;
    gtag?: any;
  }
}
// Grab the state from a global variable injected into the server-generated HTML
const preloadedState = window.__PRELOADED_STATE__;

// Allow the passed state to be garbage-collected
delete window.__PRELOADED_STATE__;

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  actionTransformer: (action) => {
    if (
      action.type === userConstants.LOGIN_SUCCESS ||
      action.type === userConstants.UPDATE_USER_SUCCESS ||
      action.user
    ) {
      // Return null to not log the action to Sentry
      return null;
    }
    return action;
  },
  stateTransformer: (state) => {
    try {
      // A shallow redact, not a deep clone: this enhancer runs on every dispatched action across
      // the whole app, so JSON.parse(JSON.stringify(state)) here was a full-state serialize on
      // every dispatch — expensive enough to visibly stall bursts of dispatches (e.g. a modal's
      // mount effects firing several actions in a row).
      if (!state.users || !state.users.user) {
        return state;
      }

      return { ...state, users: { ...state.users, user: state.users.user.emailHash } };
    } catch (e) {
      return {};
    }
  },
});

const isLocal = config.envName === 'local';

const composeEnhancers =
  // @ts-ignore
  isLocal && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ : compose;

const store = createStore(
  reducers,
  preloadedState,
  composeEnhancers(applyMiddleware(thunkMiddleware), sentryReduxEnhancer),
);
// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export default store;
